import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flow } from 'signup-constants/Flow';
import { HubletOption } from 'signup-constants/signupData/HubletOption';
import { ErrorKey } from '../app-active-error/ErrorKey';
import { VerificationType } from '../app/VerificationType';
import { ConvertGoalType } from '../convert/ConvertGoalType';
import { useConvertTrack } from '../convert/useConvertTrack';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { FlowManager } from '../flow-management/FlowManager';
import { setNextStep } from '../flow-management/setNextStep';
import { getQueryParam } from '../routing/getQueryParam';
import { Data } from '../signup-data/DATA';
import { portalSelect } from '../step-existing-accounts/handlePortalSelect';
import { setHublet, setIsNewUser, setVerificationType } from '../store/app/action-creators';
import { existingUser, redirectExistingUserAction, setPortalId } from '../store/auth/action-creators';
import { getAuthenticatedUser } from '../store/auth/selectors';
import { setError } from '../store/error/action-creators';
import { shouldSkipExistingAccountsStep } from '../store/existing-accounts/action-creators';
import { getExistingAccounts, getExistingAccountsError, isHubless } from '../store/existing-accounts/selectors';
import { bulkEmptySignupDataAndSetFresh, bulkUpdate } from '../store/signup-data/action-creators';
import { useTracker } from '../tracking/useTracker';
import { isFlow } from '../utils/isFlow';
import { Step } from '../views/Step';
import { useView } from '../views/useView';
import { useAcademyHublessUser } from './useAcademyHublessUser';
/**
 * Hook for the Existing User Screen.
 *
 * When user selects continue with this user:
 * - Updates the store with the user's details,
 * - Sets correct Error if one occurs when fetching accounts.
 * - Removes unnecessary steps (password, landing page, verification).
 *
 * When user selects Create New Account:
 * - Empties any data in the store and sets validation to Fresh.
 */

export const useExistingUser = () => {
  const dispatch = useDispatch();
  const user = useSelector(getAuthenticatedUser);
  const {
    submit
  } = useView({
    stepKey: Step.ExistingUser
  });
  const accounts = useSelector(getExistingAccounts);
  const existingAccountsError = useSelector(getExistingAccountsError);
  const userIsHubless = useSelector(isHubless);
  const Tracker = useTracker();
  const {
    startSignupForHublessUser
  } = useAcademyHublessUser();
  useConvertTrack(ConvertGoalType.ExistingUser);
  useEffect(() => {
    dispatch(setIsNewUser(false));
  }, [dispatch]);

  const continueWithThisUser = () => {
    dispatch(setVerificationType(VerificationType.ExistingUser));

    if (user) {
      dispatch(bulkUpdate({
        [Data.Email]: user.email,
        [Data.FirstName]: user.firstName,
        [Data.LastName]: user.lastName
      }));
    }

    if (existingAccountsError) {
      dispatch(setError(ErrorKey.ExistingAccountsFetchFailed));
    }

    const userOnlyFlow = getFlowConfig().isUserOnlyFlow;
    dispatch(existingUser());

    if (userOnlyFlow) {
      return dispatch(redirectExistingUserAction());
    }

    if (userIsHubless) {
      if (isFlow(Flow.Academy) || getQueryParam('hubs_signup-url') === 'chatspot') {
        return startSignupForHublessUser();
      }

      if (isFlow(Flow.StandaloneCmsPartner)) {
        return dispatch(setNextStep({
          rebuilder: FlowManager.pushNextSteps(Step.NoPartnerPortalsError)
        }));
      }

      return dispatch(setNextStep({
        rebuilder: FlowManager.pushNextSteps(Step.HublessNoAccount)
      }));
    }

    if (shouldSkipExistingAccountsStep(accounts)) {
      return dispatch(portalSelect(accounts[0].id));
    }

    return dispatch(setNextStep({
      rebuilder: FlowManager.pushNextSteps(!userIsHubless && Step.ExistingAccounts)
    }));
  };

  const createNewUser = event => {
    event.preventDefault();
    dispatch(bulkEmptySignupDataAndSetFresh([Data.FirstName, Data.LastName, Data.Email, Data.CompanyName, Data.CompanyDomain], {
      shouldEmptyPrefilledValues: false
    }));
    Tracker.signupInteraction('selected-create-new-user');
    submit();
  };

  const redirectAcademy = () => {
    if (userIsHubless) {
      return continueWithThisUser();
    }

    let bestScore = -Infinity;
    let portalId = 0;
    let hublet = HubletOption.na1;

    if (accounts) {
      accounts.forEach(account => {
        const scoringFunction = getFlowConfig().scoringFunction;
        const score = scoringFunction(account);

        if (score > bestScore) {
          portalId = account.id;
          hublet = account.hublet;
          bestScore = score;
        }
      });
    }

    Tracker.signupInteraction('embedded-academy-redirect');
    dispatch(setPortalId(portalId));
    dispatch(setHublet(hublet));
    return dispatch(redirectExistingUserAction({
      portalId,
      hublet
    }));
  };

  return {
    user,
    isHubless: userIsHubless,
    continueWithThisUser,
    createNewUser,
    redirectAcademy
  };
};